import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { toast } from "sonner";
import { RefreshCw, Trash2, Users as UsersIcon, ShieldPlus } from "lucide-react";
import { ConfirmDeleteModal } from "./ConfirmDeleteModal";

const ROLE_BADGE = {
  admin:     { label: "Admin",      className: "bg-[#FCE7F3] text-[#9D174D] border-[#FBCFE8]" },
  licencie:  { label: "Licencié",   className: "bg-[#EEF2FF] text-[#3730A3] border-[#C7D2FE]" },
};

const FILTERS = [
  { value: "all", label: "Tous" },
  { value: "admin", label: "Admins" },
  { value: "licencie_main", label: "Licenciés principaux" },
  { value: "licencie_collab", label: "Collaborateurs" },
];

function roleKind(u) {
  if (u.role === "admin") return "admin";
  if (u.role === "licencie" && u.parent_licencie_id) return "licencie_collab";
  if (u.role === "licencie") return "licencie_main";
  return u.role;
}

export function UsersPanel() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [toDelete, setToDelete] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/users");
      setUsers(data || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const filtered = useMemo(() => {
    let list = users;
    if (filter !== "all") list = list.filter((u) => roleKind(u) === filter);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter((u) =>
        (u.email || "").toLowerCase().includes(q) ||
        (`${u.prenom || ""} ${u.nom || ""}`).toLowerCase().includes(q) ||
        (u.zone || "").toLowerCase().includes(q)
      );
    }
    return list;
  }, [users, filter, search]);

  const onDelete = async () => {
    if (!toDelete) return;
    try {
      const { data } = await api.delete(`/users/${toDelete.id}`);
      const cas = data?.cascade || {};
      const extra = [];
      if (cas.detached_merchants) extra.push(`${cas.detached_merchants} commerçant(s) détaché(s)`);
      if (cas.detached_collaborators) extra.push(`${cas.detached_collaborators} collaborateur(s) détaché(s)`);
      toast.success(
        `Utilisateur supprimé${extra.length ? " — " + extra.join(", ") : ""}`,
      );
      reload();
    } catch (err) {
      toast.error(formatApiError(err));
      throw err;
    }
  };

  const consequencesFor = (u) => {
    if (u.role === "admin") {
      return ["Le compte administrateur est supprimé définitivement."];
    }
    if (u.role === "licencie" && !u.parent_licencie_id) {
      return [
        "Les commerçants du licencié restent en base mais sont détachés (à réassigner).",
        "Les collaborateurs (sous-licenciés) sont détachés également.",
        "Les leads / clients restent en base avec leur merchant_id intact.",
      ];
    }
    return ["Le compte collaborateur est supprimé définitivement."];
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-display text-2xl text-[#191D1E]">Équipe</h2>
          <p className="text-sm text-[#687175] mt-1">
            Administrateurs, licenciés principaux et collaborateurs actifs.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={reload}
          data-testid="users-refresh"
          className="rounded-lg"
        >
          <RefreshCw className="w-4 h-4 mr-2" /> Rafraîchir
        </Button>
        <Button
          size="sm"
          onClick={() => {
            window.dispatchEvent(new CustomEvent("pde:invite-admin"));
          }}
          data-testid="users-invite-admin"
          className="rounded-lg bg-[#1B4FA6] hover:bg-[#143C82] text-white font-semibold ml-2"
        >
          <ShieldPlus className="w-4 h-4 mr-2" /> Inviter un admin collaborateur
        </Button>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex flex-wrap gap-1">
          {FILTERS.map((f) => (
            <button
              key={f.value}
              data-testid={`users-filter-${f.value}`}
              onClick={() => setFilter(f.value)}
              className={[
                "px-3 py-1.5 rounded-full text-sm transition-colors border",
                filter === f.value
                  ? "bg-[#1B4FA6] text-white border-[#1B4FA6]"
                  : "bg-white text-[#1F2937] border-[#E5E7EB] hover:bg-[#F5F6F9]",
              ].join(" ")}
            >
              {f.label}
            </button>
          ))}
        </div>
        <Input
          data-testid="users-search"
          placeholder="Rechercher un nom, email, agence…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-xs ml-auto rounded-lg"
        />
      </div>

      {loading ? (
        <div className="py-12 text-center text-sm text-[#939BA0]">Chargement…</div>
      ) : filtered.length === 0 ? (
        <div className="py-12 text-center text-sm text-[#939BA0]" data-testid="users-empty">
          <UsersIcon className="w-8 h-8 mx-auto mb-3 text-[#BFC6CC]" />
          Aucun utilisateur avec ce filtre.
        </div>
      ) : (
        <div className="overflow-x-auto -mx-2">
          <table className="w-full text-[13px]" data-testid="users-table">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-[#939BA0] border-b border-[#E5E7EB]">
                <th className="px-2 py-2">Email</th>
                <th className="px-2 py-2">Nom</th>
                <th className="px-2 py-2">Rôle</th>
                <th className="px-2 py-2">Agence / Zone</th>
                <th className="px-2 py-2">Rattachement</th>
                <th className="px-2 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((u) => {
                const kind = roleKind(u);
                const badge = ROLE_BADGE[u.role] || { label: u.role, className: "" };
                const subLabel =
                  kind === "licencie_collab" ? "Collaborateur" :
                  kind === "licencie_main" ? "Principal" :
                  kind === "admin" ? "Siège" : "";
                const isSelf = u.id === me?.id;
                return (
                  <tr key={u.id} className="border-b border-[#EEF0F4] last:border-0 hover:bg-[#FAFBFC]" data-testid={`user-row-${u.id}`}>
                    <td className="px-2 py-2 font-medium text-[#191D1E]">{u.email}</td>
                    <td className="px-2 py-2 text-[#687175]">{u.prenom} {u.nom}</td>
                    <td className="px-2 py-2">
                      <div className="flex flex-col gap-0.5">
                        <Badge className={`${badge.className} border w-fit text-[11px] px-2 py-0.5`}>{badge.label}</Badge>
                        {subLabel && <span className="text-[10px] text-[#939BA0] uppercase tracking-wide">{subLabel}</span>}
                      </div>
                    </td>
                    <td className="px-2 py-2 text-[#687175]">{u.zone || <span className="text-[#BFC6CC]">—</span>}</td>
                    <td className="px-2 py-2 text-[#687175]">{u.responsable || <span className="text-[#BFC6CC]">—</span>}</td>
                    <td className="px-2 py-2 text-right whitespace-nowrap">
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={isSelf}
                        onClick={() => setToDelete(u)}
                        data-testid={`user-delete-${u.id}`}
                        className="rounded-lg text-[#B91C1C] border-[#FECACA] hover:bg-[#FEF2F2] disabled:opacity-30"
                        title={isSelf ? "Vous ne pouvez pas vous supprimer vous-même" : "Supprimer cet utilisateur"}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmDeleteModal
        open={!!toDelete}
        onOpenChange={(v) => !v && setToDelete(null)}
        entityType={
          toDelete?.role === "admin" ? "administrateur" :
          toDelete?.parent_licencie_id ? "collaborateur licencié" :
          "licencié principal"
        }
        entityLabel={toDelete?.email || ""}
        description={
          toDelete?.role === "admin"
            ? `Vous êtes sur le point de supprimer l'administrateur ${toDelete?.prenom || ""} ${toDelete?.nom || ""}.`
            : `Vous êtes sur le point de supprimer ${toDelete?.prenom || ""} ${toDelete?.nom || ""}.`
        }
        consequences={toDelete ? consequencesFor(toDelete) : []}
        onConfirm={onDelete}
      />
    </div>
  );
}

export default UsersPanel;
